import React from 'react';
import { Brain, Zap, LineChart, Bell, Quote, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { PILLARS, MENTOR } from '../mock';
import { useCountry, useCtaHandler } from '../context/CountryContext';
import FadeInSection from './FadeInSection';
import SafeMotion from './SafeMotion';

const PillarIcon = ({ name }) => {
  const cls = 'w-5 h-5';
  if (name === 'brain') return <Brain className={cls} />;
  if (name === 'zap') return <Zap className={cls} />;
  if (name === 'chart') return <LineChart className={cls} />;
  if (name === 'bell') return <Bell className={cls} />;
  return null;
};

export default function Ecosystem() {
  const { telegramUrl } = useCountry();
  const track = useCtaHandler();
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      {/* Premium dark background with neon */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(900px 500px at 50% 0%, rgba(255,122,47,0.16), transparent 60%)' }} />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute -left-40 top-1/3 w-[420px] h-[420px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,122,47,0.16), transparent 65%)' }} />
        <div className="absolute -right-40 bottom-10 w-[420px] h-[420px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,122,47,0.12), transparent 65%)' }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Tag */}
        <FadeInSection className="flex justify-center">
          <div className="liquid-glass rounded-full inline-flex items-center gap-2 px-3.5 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a2f] relative z-[1]" />
            <span className="text-[11.5px] uppercase tracking-[0.18em] text-white/90 relative z-[1]">The MBE Core Support System</span>
          </div>
        </FadeInSection>

        {/* Heading */}
        <FadeInSection delay={0.1} className="mt-6 text-center max-w-4xl mx-auto">
          <h2 className="font-display font-extrabold text-white text-4xl md:text-[54px] leading-[1.05] tracking-tight">
            You Are Not Trading Alone.
            <br />
            <span className="text-orange-gradient">Zack &amp; The MBE Ecosystem</span> Guide You Every Day.
          </h2>
          <p className="mt-6 text-[#f2f2f2] text-[15.5px] leading-relaxed">
            Most traders fail because they trade alone with no direction. At MBE, Zack has built a full ecosystem that combines expert coaching with smart automation so you can execute with confidence, hit every milestone, and win your Bali trip.
          </p>
        </FadeInSection>

        {/* Mentor profile */}
        <FadeInSection delay={0.15} className="mt-14 card-dark p-4 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-center">
          <div className="md:col-span-5 relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[4/4]">
            <img loading="lazy" decoding="async" src={MENTOR.image} alt={MENTOR.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,11,0) 40%, rgba(10,10,11,0.85) 100%)' }} />
            <div className="absolute left-4 bottom-4 flex items-center gap-2 nav-glass rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a2f]" />
              <span className="text-white/90 text-[12px]">Live Masterclass</span>
            </div>
          </div>
          <div className="md:col-span-7 p-2 md:p-4">
            <div className="icon-badge"><Quote className="w-5 h-5" /></div>
            <blockquote className="mt-5 font-display normal-case text-white text-2xl md:text-3xl leading-snug tracking-tight" style={{ fontWeight: 500 }}>
              &ldquo;{MENTOR.quote}&rdquo;
            </blockquote>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-[2px] w-10 bg-gradient-to-r from-[#ff7a2f] to-transparent" />
              <div>
                <div className="text-white font-semibold">{MENTOR.name}</div>
                <div className="text-[#f2f2f2] text-[13px]">{MENTOR.role}</div>
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* Pillars header */}
        <FadeInSection delay={0.05} className="mt-16 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11.5px] uppercase tracking-[0.18em] text-[#ff9b5a]">The 4 Pillar Execution Suite</div>
            <h3 className="mt-2 font-display font-bold text-white text-2xl md:text-3xl">Everything you need. Nothing you don&apos;t.</h3>
          </div>
          <div className="text-[#f2f2f2] text-[13px] max-w-sm">A complete stack of tools, education and real-time intelligence built specifically for Silver traders.</div>
        </FadeInSection>

        {/* Pillar cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {PILLARS.map((p, i) => (
            <SafeMotion
              key={p.title}
              initial={{ filter: 'blur(10px)', opacity: 0, y: 30 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1, margin: '0px 0px -80px 0px' }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: i * 0.12 }}
              className="card-dark overflow-hidden flex flex-col"
            >
              {p.image ? (
                <div className="relative h-56 overflow-hidden">
                  <img loading="lazy" decoding="async" src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,11,0.05) 0%, rgba(10,10,11,0.9) 100%)' }} />
                  <div className="absolute top-4 left-4 icon-badge"><PillarIcon name={p.icon} /></div>
                  <div className="absolute bottom-3 left-5 text-[11px] uppercase tracking-[0.18em] text-[#f2f2f2]">{p.tag}</div>
                </div>
              ) : (
                <div className="relative h-40 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(255,122,47,0.14), rgba(255,122,47,0.03))' }}>
                  <div className="absolute inset-0 grid-bg opacity-40" />
                  <div className="absolute top-4 left-4 icon-badge"><PillarIcon name={p.icon} /></div>
                  <div className="absolute bottom-3 left-5 text-[11px] uppercase tracking-[0.18em] text-[#f2f2f2]">{p.tag}</div>
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#ff9b5a] font-semibold">Pillar {String(i + 1).padStart(2, '0')}</div>
                </div>
                <div className="mt-1 text-white font-semibold text-[17px] uppercase tracking-wide">{p.title}</div>
                <p className="mt-2 text-[#f2f2f2] text-[13.5px] leading-relaxed flex-1">{p.desc}</p>
                <div className="mt-5 flex items-center gap-2 text-[13px]">
                  <CheckCircle2 className="w-4 h-4 text-[#ff7a2f]" />
                  <span className="text-[#f2f2f2]">Included when you join</span>
                </div>
              </div>
            </SafeMotion>
          ))}
        </div>

        <FadeInSection delay={0.1} className="mt-12 text-center">
          <a href={telegramUrl} target="_blank" rel="noreferrer" onClick={() => track('ecosystem-cta')} className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 text-[14.5px]">
            Unlock the Silver Ecosystem <ArrowUpRight className="w-4 h-4" />
          </a>
        </FadeInSection>
      </div>
    </section>
  );
}
